import { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { request } from "../../../utils/api";
import { formatYYYYMMDD, getTodayDate } from "../../../utils";
import { toast, ToastContainer } from "react-toastify";
import { AuthContext } from "../../../auth/AuthContext";
import Pagination from "../utilComponent/Pagination";
import Loading from "../utilComponent/Loading";

const Leasure = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const canManage = ["super_admin", "admin"].includes(user?.role_key);
  const [page, setPage] = useState(1);
  const [loans, setLoans] = useState([]);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sheetForm, setSheetForm] = useState({
    title: "",
    description: "",
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
  });
  const [formData, setFormData] = useState({
    email: "",
    access: "view",
  });

  const openCreateModal = () => {
    setSheetForm({ title: "", description: "" });
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
  };

  const submitCreateSheet = async () => {
    if (!sheetForm.title.trim()) {
      toast.error("Title is required");
      return;
    }

    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("title", sheetForm.title);
      fd.append("description", sheetForm.description);
      if (user?.role === "super_admin") {
        fd.append("account_id", user.account.id);
      }

      const res = await request({
        url: "sheets",
        method: "POST",
        data: fd,
      });

      if (res?.status) {
        toast.success("Sheet created successfully");
        closeCreateModal();
        fetchSheets(page);
      }
    } catch {
      toast.error("Failed to create sheet");
    } finally {
      setLoading(false);
    }
  };

  const fetchSheets = async (pageNumber = 1) => {
    try {
      setLoading(true);

      const res = await request({
        url: `sheets?page=${page}&per_page=${pagination.per_page}`,
        method: "GET",
        params: {
          page: pageNumber,
        },
      });

      if (res?.status) {
        setLoans(res.data.items);
        setPagination({
          current_page: res.data.pagination.current_page,
          per_page: res.data.pagination.per_page,
          total: res.data.pagination.total,
          last_page: res.data.pagination.last_page,
        });
        setPage(pageNumber);
      }
    } catch (error) {
      toast.error("Failed to load loans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSheets(1);
  }, []);

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > pagination.last_page) return;
    fetchSheets(pageNumber);
  };

  const handleSubmit = async () => {
    if (!formData.email.trim()) {
      toast.error("Email is required");
      return;
    }

    if (!selectedLoan?.id) {
      toast.error("Invalid sheet selected");
      return;
    }

    try {
      setLoading(true);

      const fd = new FormData();
      fd.append("email", formData.email);
      fd.append("permission", formData.access);

      const res = await request({
        url: `sheets/${selectedLoan.id}/share`,
        method: "POST",
        data: fd,
      });

      if (res?.status) {
        toast.success("Access granted successfully");
        closeModal();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to grant access");
    } finally {
      setLoading(false);
    }
  };

  const openModal = (loan) => {
    setSelectedLoan(loan);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedLoan(null);
    setFormData({ email: "", access: "view" });
  };

 const getAccessLabel = (access) => {
  const labels = {
    edit: "Edit & View",
    view: "View",
  };

  return labels[access] || "Unknown Access";
 };
  
  return (
    <>
    <ToastContainer/>
    <div className="container-fluid p-0">
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
        <ol className="breadcrumb bg-transparent mb-0">
          <li className="breadcrumb-item">
            <Link to="/">Home</Link>
          </li>
          <li className="breadcrumb-item">Calculation</li>
          <li className="breadcrumb-item active">Leasure</li>
        </ol>
      </div>

      <div className="card mx-4">
        {canManage && <div className="d-flex justify-content-end p-3">
          <button className="btn btn-primary" onClick={openCreateModal} disabled={loading}>
            Create Sheet
          </button>
        </div>}

        <hr />
        <div className="row g-3 px-4 pb-4">
          {loading ? <Loading/> : loans.length === 0 ? (
            <div className="text-center text-muted py-5">No sheets found</div>
          ) : (
            loans.map((loan) => (
              <div className="col-3" key={loan.id}>
                <div className="card h-100">
                  <div className="card-body">
                    <p>
                      <strong>Title:</strong> {loan?.title || "Untitled"}
                    </p>
                    <p>
                      <strong>Description:</strong>{" "}
                      {loan?.description
                        ? loan.description.length > 40
                          ? loan.description.slice(0, 40) + "..."
                          : loan.description
                        : "N/A"}
                    </p>
                    <p>
                      <strong>Date:</strong> {formatYYYYMMDD(loan?.created_at)}
                    </p>
                    <button
                      className="btn btn-outline-primary w-100 mb-2"
                      onClick={() =>
                        navigate(`/leasure-list-detail/${loan.id}`)
                      }
                    >
                      View Detail
                    </button>

                    {canManage && <button
                      className="btn btn-success w-100"
                      onClick={() => openModal(loan)}
                    >
                      Share Access
                    </button>}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <Pagination
          currentPage={pagination.current_page}
          lastPage={pagination.last_page}
          onPageChange={handlePageChange}
        />
      </div>
      {showModal && (
        <>
          <div className="modal fade show d-block">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Grant Access</h5>
                  <button className="btn-close" onClick={closeModal}></button>
                </div>

                <div className="modal-body">
                  <div className="mb-3">
                    <label>Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                    />
                  </div>

                  <label className="fw-bold">Access Level</label>

                  {["view", "edit"].map((val) => (
                    <div className="form-check" key={val}>
                      <input
                        type="radio"
                        className="form-check-input"
                        checked={formData.access === val}
                        onChange={() =>
                          setFormData({ ...formData, access: val })
                        }
                      />
                      <label className="form-check-label">
                        {getAccessLabel(val)}
                      </label>
                    </div>
                  ))}
                </div>

                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? "Saving..." : "Grant Access"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      {showCreateModal && (
        <>
          <div className="modal fade show d-block">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Create Sheet</h5>
                  <button className="btn-close" onClick={closeCreateModal} />
                </div>

                <div className="modal-body">
                  <div className="mb-3">
                    <label>Title</label>
                    <input
                      className="form-control"
                      value={sheetForm.title}
                      onChange={(e) =>
                        setSheetForm({ ...sheetForm, title: e.target.value })
                      }
                    />
                  </div>

                  <div className="mb-3">
                    <label>Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={sheetForm.description}
                      onChange={(e) =>
                        setSheetForm({
                          ...sheetForm,
                          description: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={closeCreateModal}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={submitCreateSheet}
                    disabled={loading}
                  >
                    {loading ? "Saving..." : "Create"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
    </>
  );
};

export default Leasure;
